import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';

import { AuthError, beginTotpEnrolment, completeTotpEnrolment } from '../auth/cognito.ts';
import { Button } from '../components/button.tsx';
import { Wordmark } from '../components/brand.tsx';
import { Field } from '../components/field.tsx';

// The screen the API's `mfa-required` refusal sends you to.
//
// Cognito never asks for this: pool MFA is `OPTIONAL`, the only setting it accepts alongside
// `EMAIL_OTP` (ADR-0006 rule 3), so a first sign-in completes with no second factor and no offer to
// set one up. AVenue requires it in the API guard, and this is how a person satisfies that.
//
// Enrolment talks to Cognito directly, not through the API — which is what makes it reachable while
// every API call is being refused.

export function EnrolTotpScreen({ onEnrolled }: { onEnrolled: () => void }) {
  const [setup, setSetup] = useState<{ uri: string; secret: string } | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  // Associating a token is a **mutation**: every call mints a new secret and supersedes the one
  // Cognito is waiting on. React's StrictMode runs effects twice in development, so without this
  // guard two associations race, the screen shows one secret and Cognito expects a code from the
  // other, and every code entered is rejected. The usual `cancelled` flag does not help — it stops
  // a stale result being displayed, not a second request being sent.
  const started = useRef(false);

  const associate = useCallback(async () => {
    setBusy(true);
    setError(undefined);
    setSetup(null);
    setCode('');
    try {
      setSetup(await beginTotpEnrolment());
    } catch (caught) {
      setError(caught instanceof AuthError ? caught.message : 'Could not start setup.');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void associate();
  }, [associate]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await completeTotpEnrolment(code.trim());
      onEnrolled();
    } catch (caught) {
      setError(caught instanceof AuthError ? caught.message : 'Could not confirm that code.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-6 p-6">
      <div className="flex flex-col gap-4 border-b-2 border-divider pb-5">
        {/* The wordmark alone: the icon never sits beside it (brand pack v1.1). */}
        <Wordmark height={22} />
        <div>
          <h1 className="text-[28px] text-ink">Set up your authenticator app</h1>
        </div>
      </div>
      <p className="text-ink-soft">
        AVenue needs a second factor before this account can be used. Open your authenticator app,
        add an account, then enter the six-digit code it shows.
      </p>

      {error ? (
        <p role="alert" className="alert">
          {error}
        </p>
      ) : null}

      {setup ? (
        <form className="flex flex-col gap-4" onSubmit={submit}>
          <a
            href={setup.uri}
            className="text-accent-700 underline focus-visible:outline-2 focus-visible:outline-offset-2"
          >
            Open in your authenticator app
          </a>
          <div>
            <p className="text-sm font-medium text-ink">Or type this setup key in by hand</p>
            <code className="mt-1 block rounded bg-tile p-3 font-mono text-sm break-all text-ink">
              {setup.secret}
            </code>
          </div>
          <Field
            label="Six-digit code from the app"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            required
            value={code}
            onChange={(e) => setCode(e.currentTarget.value)}
          />
          <Button type="submit" disabled={busy}>
            {busy ? 'Checking…' : 'Continue'}
          </Button>
          <Button type="button" variant="quiet" disabled={busy} onClick={() => void associate()}>
            Start again with a new setup key
          </Button>
        </form>
      ) : error ? null : (
        <p className="text-muted">Preparing…</p>
      )}
    </main>
  );
}
